import PageTransition from '../components/PageTransition';
import ScrollReveal from '../components/ScrollReveal';
import { useSEO } from '../hooks/useSEO';
import { PAGE_SEO } from '../utils/seo';

export default function PrivacyPolicyPage() {
  // Apply SEO
  useSEO(PAGE_SEO.privacy);

  return (
    <PageTransition>
      <div className="container" style={{ maxWidth: '800px', paddingTop: 'var(--space-8)', paddingBottom: 'var(--space-12)' }}>
        <ScrollReveal>
          <header style={{ marginBottom: 'var(--space-10)' }}>
            <h1 className="h-1" style={{ marginBottom: 'var(--space-4)' }}>Privacy Policy</h1>
            <p className="body-lg" style={{ color: 'var(--text-secondary)' }}>
              Last updated: April 13, 2026
            </p>
          </header>
        </ScrollReveal>

        <ScrollReveal>
          <section style={{ marginBottom: 'var(--space-8)' }}>
            <h2 className="h-2" style={{ marginBottom: 'var(--space-4)', color: 'var(--accent-green)' }}>Our Commitment to Privacy</h2>
            <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.8, marginBottom: 'var(--space-4)' }}>
              At Siak Studios, privacy isn't just a feature — it's a fundamental right. We design our products and our business practices to collect minimal data, protect what we do collect, and be transparent about our practices. This policy explains exactly what information we collect, how we use it, and your rights regarding your data.
            </p>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section style={{ marginBottom: 'var(--space-8)' }}>
            <h2 className="h-3" style={{ marginBottom: 'var(--space-4)', color: 'var(--text-warning)' }}>Information We Collect</h2>
            
            <h3 className="h-4" style={{ marginBottom: 'var(--space-3)', marginTop: 'var(--space-6)' }}>Information You Provide</h3>
            <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.7, marginBottom: 'var(--space-4)' }}>
              When you contact us through our website, we collect:
            </p>
            <ul style={{ paddingLeft: 'var(--space-6)', marginBottom: 'var(--space-4)', color: 'var(--text-near-white)', lineHeight: 1.7 }}>
              <li>Your name</li>
              <li>Your email address</li>
              <li>Project type and budget information</li>
              <li>Any message content you choose to share</li>
            </ul>

            <h3 className="h-4" style={{ marginBottom: 'var(--space-3)', marginTop: 'var(--space-6)' }}>Automatic Information</h3>
            <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.7, marginBottom: 'var(--space-4)' }}>
              Like most websites, we may collect certain information automatically when you visit our site, including:
            </p>
            <ul style={{ paddingLeft: 'var(--space-6)', marginBottom: 'var(--space-4)', color: 'var(--text-near-white)', lineHeight: 1.7 }}>
              <li>IP address (anonymized)</li>
              <li>Browser type and version</li>
              <li>Pages visited and time spent on the site</li>
              <li>Referring website</li>
            </ul>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section style={{ marginBottom: 'var(--space-8)' }}>
            <h2 className="h-3" style={{ marginBottom: 'var(--space-4)', color: 'var(--accent-green)' }}>How We Use Your Information</h2>
            <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.7, marginBottom: 'var(--space-4)' }}>
              We use the information we collect solely for:
            </p>
            <ul style={{ paddingLeft: 'var(--space-6)', marginBottom: 'var(--space-4)', color: 'var(--text-near-white)', lineHeight: 1.7 }}>
              <li>Responding to your inquiries and project requests</li>
              <li>Improving our website and services</li>
              <li>Understanding how visitors interact with our site</li>
              <li>Communicating with you about projects</li>
            </ul>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section style={{ marginBottom: 'var(--space-8)' }}>
            <h2 className="h-3" style={{ marginBottom: 'var(--space-4)', color: 'var(--text-announcement)' }}>Data Protection</h2>
            <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.7, marginBottom: 'var(--space-4)' }}>
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. All contact form submissions are securely transmitted to our Google Sheets backend with industry-standard encryption.
            </p>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section style={{ marginBottom: 'var(--space-8)' }}>
            <h2 className="h-3" style={{ marginBottom: 'var(--space-4)', color: 'var(--accent-green)' }}>Third-Party Services</h2>
            <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.7, marginBottom: 'var(--space-4)' }}>
              Our website uses the following third-party services:
            </p>
            <ul style={{ paddingLeft: 'var(--space-6)', marginBottom: 'var(--space-4)', color: 'var(--text-near-white)', lineHeight: 1.7 }}>
              <li><strong style={{ color: 'var(--text-near-white)' }}>Google Fonts</strong> — For typography (subject to Google's privacy policy)</li>
              <li><strong style={{ color: 'var(--text-near-white)' }}>Google Apps Script</strong> — For secure form submission handling</li>
            </ul>
            <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.7, marginBottom: 'var(--space-4)' }}>
              We do not use analytics, tracking pixels, advertising networks, or any form of user behavior tracking.
            </p>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section style={{ marginBottom: 'var(--space-8)' }}>
            <h2 className="h-3" style={{ marginBottom: 'var(--space-4)', color: 'var(--text-warning)' }}>Your Rights</h2>
            <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.7, marginBottom: 'var(--space-4)' }}>
              You have the right to:
            </p>
            <ul style={{ paddingLeft: 'var(--space-6)', marginBottom: 'var(--space-4)', color: 'var(--text-near-white)', lineHeight: 1.7 }}>
              <li>Request access to your personal data we hold</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to processing of your personal data</li>
              <li>Request restriction of processing your personal data</li>
              <li>Request transfer of your personal data to you or a third party</li>
            </ul>
            <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.7, marginBottom: 'var(--space-4)' }}>
              To exercise any of these rights, please contact us at <a href="mailto:hello@siak.in" style={{ color: 'var(--accent-green)' }}>hello@siak.in</a>
            </p>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section style={{ marginBottom: 'var(--space-8)' }}>
            <h2 className="h-3" style={{ marginBottom: 'var(--space-4)', color: 'var(--accent-green)' }}>Data Retention</h2>
            <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.7, marginBottom: 'var(--space-4)' }}>
              We retain contact form submissions for as long as necessary to respond to inquiries and for our records. We do not sell, trade, or otherwise transfer your personal information to outside parties.
            </p>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section style={{ marginBottom: 'var(--space-8)' }}>
            <h2 className="h-3" style={{ marginBottom: 'var(--space-4)', color: 'var(--text-announcement)' }}>Children's Privacy</h2>
            <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.7, marginBottom: 'var(--space-4)' }}>
              Our website is not directed to individuals under the age of 16. We do not knowingly collect personal information from children. If we become aware that we have inadvertently collected data from a child, we will take steps to delete such information.
            </p>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section style={{ marginBottom: 'var(--space-8)' }}>
            <h2 className="h-3" style={{ marginBottom: 'var(--space-4)', color: 'var(--accent-green)' }}>Changes to This Policy</h2>
            <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.7, marginBottom: 'var(--space-4)' }}>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page with an updated "Last updated" date.
            </p>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section style={{ marginBottom: 'var(--space-8)' }}>
            <h2 className="h-3" style={{ marginBottom: 'var(--space-4)', color: 'var(--text-warning)' }}>Contact Us</h2>
            <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.7, marginBottom: 'var(--space-4)' }}>
              If you have any questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            <ul style={{ paddingLeft: 'var(--space-6)', marginBottom: 'var(--space-4)', color: 'var(--text-near-white)', lineHeight: 1.7 }}>
              <li><strong style={{ color: 'var(--text-near-white)' }}>Email:</strong> <a href="mailto:hello@siak.in" style={{ color: 'var(--accent-green)' }}>hello@siak.in</a></li>
              <li><strong style={{ color: 'var(--text-near-white)' }}>Website:</strong> <a href="https://siak.in/contact" style={{ color: 'var(--accent-green)' }}>siak.in/contact</a></li>
            </ul>
          </section>
        </ScrollReveal>
      </div>
    </PageTransition>
  );
}
